/**
 * ExcursionModal Component
 * Displays detailed information about excursion upgrades with icons
 * WHY: Follows Figma specs with icon rows for price, duration, passengers, transportation
 */

import React from "react";
import { Stack, Box, Typography } from "@mui/material";
import {
  Close,
  ConfirmationNumber,
  Schedule,
  PeopleAlt,
  DirectionsBus
} from "@mui/icons-material";
import { useTranslations } from "next-intl";
import {ColletteChip, ColletteIconButton} from "@collette-travel/collette-common-controls";
import Image from "next/image";
import type { Excursion } from "@/types/dto/pdp/pdp-itinerary";

export interface ExcursionModalProps {
  excursion: Excursion;
  handleClose: () => void;
}

const ExcursionModal: React.FC<ExcursionModalProps> = ({
  excursion,
  handleClose
}) => {
  const t = useTranslations("PDP");

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default",
      position: "relative",
      padding: {xs: "2.4rem", md: "4rem 6rem"},
      gap: "2.4rem"
      // WHY: Matches Figma modal padding (60px = 6rem, 40px = 4rem)
      // WHY: 24px gap between image/content and description per Figma
    }}
  >
    {/* Close Button */}
    <ColletteIconButton
      onClick={handleClose}
      testId="excursion-modal-close-button"
      sx={{
        position: "absolute",
        top: {xs: 8, md: 24},
        right: {xs: 8, md: 24},
        zIndex: 1
      }}
    >
      <Close />
    </ColletteIconButton>

    {/* Main Content: Image + Details */}
      <Stack
        sx={{
          display: "flex",
          gap: "2rem",
          alignItems: "flex-start",
          flexDirection: {xs: "column-reverse", md: "row"}
        }}
      >
      {/* Left Column: Image */}
        {excursion.imageUrl && (
          <Box
            sx={{
              position: "relative",
              width: {xs: "100%", md: "30rem"},
              height: "20rem",
              borderRadius: "2rem",
              overflow: "hidden",
              flexShrink: 0
              // WHY: Fixed 300px × 200px image from Figma
            }}
          >
            <ColletteChip
              color="info"
              sx={{
                fontSize: "1.6rem !important",
                position: "absolute",
                top: 12,
                left: 12,
                zIndex: 5,
                fontWeight: 500,
                borderRadius: "1rem"
              }}
              testId="optional-add-on-chip"
              label={t("Optional Add-on")}
            />
            <Image
              src={excursion.imageUrl}
              data-testid="excursion-image"
              alt={excursion.title}
              fill
              sizes="(max-width: 900px) 100vw, 300px"
              style={{ objectFit: "cover" }}
            />
          </Box>
        )}

        {/* Right Column: Title + Icon Details + Description */}
        <Stack
          gap="2.4rem"
          flex={1}
        // WHY: 24px gap between title and details section per Figma
        >
          {/* Title */}
          <Typography
            component="h2"
            data-testid="excursion-title"
            sx={{
              fontSize: "2rem",
              fontWeight: 600,
              lineHeight: 1.3,
              color: "primary.main",
              margin: 0
              // WHY: H5 Desktop from Figma (20px, semibold, Primary/Blue)
            }}
          >
            {excursion.title}
          </Typography>

          {/* Icon Detail Rows + Description */}
          <Stack gap="2.4rem">
            {/* Icon Detail Rows */}
            <Stack gap="1.2rem">
              {/* Price */}
              {excursion.price ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.8rem",
                    alignItems: "center"
                    // WHY: 8px gap between icon and text per Figma
                  }}
                >
                  <ConfirmationNumber
                    data-testid="excursion-price-icon"
                    sx={{
                      fontSize: "2.4rem",
                      color: "text.primary"
                    }}
                  />
                  <Typography
                    data-testid="excursion-price"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 500,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px"
                      // WHY: Subtitle 2 from Figma (16px, medium, tracking 0.4px)
                    }}
                  >
                    {excursion.price} {t("per person")}
                  </Typography>
                </Box>
              ) : null}

              {/* Duration */}
              {excursion.time && parseInt(excursion.time, 10) > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.8rem",
                    alignItems: "center"
                  }}
                >
                  <Schedule
                    data-testid="excursion-duration-icon"
                    sx={{ fontSize: "2.4rem", color: "text.primary" }}
                  />
                  <Typography
                  data-testid="excursion-duration"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 500,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px"
                    }}
                  >
                    {excursion.time} {t("Hours")}
                  </Typography>
                </Box>
              ) : null}

              {/* Passengers */}
              {excursion.passengers && parseInt(excursion.passengers, 10) > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.8rem",
                    alignItems: "center"
                  }}
                >
                  <PeopleAlt
                    data-testid="excursion-passengers-icon"
                    sx={{ fontSize: "2.4rem", color: "text.primary" }}
                  />
                  <Typography
                    data-testid="excursion-passengers"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 500,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px"
                    }}
                  >
                    {excursion.passengers} {t("Passengers Minimum")}
                  </Typography>
                </Box>
              ) : null}

              {/* Transportation */}
              {excursion.transportation ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: "0.8rem",
                    alignItems: "center"
                  }}
                >
                  <DirectionsBus
                    data-testid="excursion-transportation-icon"
                    sx={{ fontSize: "2.4rem", color: "text.primary" }}
                  />
                  <Typography
                    data-testid="excursion-transportation"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 500,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px"
                    }}
                  >
                    {excursion.transportation ? t("Transportation Included") : t("Transportation Not Included")}
                  </Typography>
                </Box>
              ) : null}
            </Stack>

            {/* Description */}
            <Typography
              data-testid="excursion-description"
              sx={{
                fontSize: "1.6rem",
                fontWeight: 400,
                lineHeight: 1.6,
                color: "text.primary"
                // WHY: Body 3 Regular from Figma (16px, regular, 1.6 line height)
              }}
            >
              {excursion.fullDescription}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
  </Stack>
  );
};

export default ExcursionModal;